import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import React from "react";
import { CompetitionService, FixtureService, SeasonService, SponsorService, TeamService, VenueService } from "src/app/services/Index";
import "./CreateFixture.css";

type Props = {
	visible: boolean;
	fixture?: any;
	onClose: () => void;
	onSaved: (fixtureId: number) => void;
	onError?: (message: string) => void;
};

type State = {
	saving: boolean;
	seasons: any[];
	competitions: any[];
	teams: any[];
	venues: any[];
	sponsors: any[];
	form: any;
};

const COMPETITION_TYPES = [
	{ label: "League", value: 0 },
	{ label: "Cup", value: 10 },
	{ label: "Friendly", value: 20 },
	{ label: "Pre-season", value: 30 },
	{ label: "Play-off", value: 40 },
	{ label: "Other", value: 50 }
];

const LOCATIONS = [
	{ label: "Home", value: 0 },
	{ label: "Away", value: 10 },
	{ label: "Neutral", value: 20 }
];

/**
 * Three states, not two. "Follow the competition" is the default and is
 * genuinely different from either answer — it means the club hasn't made a
 * decision about this fixture and the competition's rule applies.
 */
const SEASON_TICKET_OPTIONS = [
	{ label: "Follow the competition", value: null },
	{ label: "Admit season ticket holders", value: true },
	{ label: "Season tickets not valid", value: false }
];

const AMENITIES = [
	{ key: "hasFoodAndDrink", label: "Food and drink" },
	{ key: "hasMerchandise", label: "Merchandise" },
	{ key: "hasParking", label: "Parking" },
	{ key: "hasAccessibleEntrances", label: "Accessible entrances" },
	{ key: "hasAccessibleToilets", label: "Accessible toilets" },
	{ key: "hasCashMachine", label: "Cash machine" },
	{ key: "hasInternet", label: "Internet" }
];

/**
 * Creating a fixture from the ticketing side.
 *
 * Grouped the way a club thinks about it — admission first, because who can
 * get in is the decision that everything else hangs off, and it's the one the
 * old scoreboard form never asked. Scores, cards and half-time draws stay on
 * the matchday form; nobody sets those when they're putting a game on sale.
 */
export class CreateFixture extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			saving: false, seasons: [], competitions: [], teams: [], venues: [], sponsors: [],
			form: this.blank()
		};
	}

	componentDidMount() {
		this.loadOptions();
	}

	componentDidUpdate(prev: Props) {
		if (!prev.visible && this.props.visible) {
			this.setState({ form: this.props.fixture ? this.fromFixture(this.props.fixture) : this.blank() });
			this.loadOptions().then(() => {
				// The list endpoint carries enough to label a fixture, not
				// enough to edit one — amenities, sponsors and the streaming
				// link only come back on the full record.
				if (this.props.fixture?.id) this.loadFull(this.props.fixture.id);
			});
		}
	}

	blank() {
		const kickOff = new Date();
		kickOff.setDate(kickOff.getDate() + 7);
		kickOff.setHours(15, 0, 0, 0);
		return {
			admitSeasonTickets: null,
			competitionTypeId: 0,
			fixtureDateTime: kickOff,
			hasAccessibleEntrances: true,
			hasAccessibleToilets: true,
			hasFoodAndDrink: true,
			hasParking: true,
			location: 0,
			showOnSchedule: true
		} as any;
	}

	fromFixture(fixture: any) {
		// The competition dropdown filters by type, so an existing fixture has
		// to arrive with the type its competition belongs to — otherwise the
		// list comes up empty and the competition looks unset.
		const competition = this.state?.competitions?.find((c: any) => c.id === fixture.competitionId);
		return {
			...fixture,
			competitionTypeId: competition ? competition.competitionType : null,
			fixtureDateTime: new Date(fixture.fixtureDateTime)
		};
	}

	async loadFull(id: number) {
		try {
			const res = await FixtureService.getSingleById(id);
			if (!res.ok) return;
			const full = await res.json();
			if (this.props.visible) this.setState({ form: this.fromFixture(full) });
		} catch {
			// Keep what the list gave us; the form is still usable.
		}
	}

	async loadOptions() {
		const safe = async (fn: () => Promise<Response>) => {
			try {
				const res = await fn();
				if (!res.ok) return [];
				const body = await res.json();
				return Array.isArray(body) ? body : [];
			} catch {
				return [];
			}
		};

		const [seasons, competitions, teams, venues, sponsors] = await Promise.all([
			safe(() => SeasonService.getAll()),
			safe(() => CompetitionService.getAll()),
			safe(() => TeamService.getAll()),
			safe(() => VenueService.getAll()),
			safe(() => SponsorService.getAll())
		]);
		this.setState({ seasons, competitions, teams, venues, sponsors });
	}

	set(patch: any) {
		this.setState({ form: { ...this.state.form, ...patch } });
	}

	async save() {
		const f = this.state.form;
		if (!f.teamId) {
			this.props.onError?.("Pick the opposition.");
			return;
		}
		if (!f.fixtureDateTime) {
			this.props.onError?.("Pick a kick-off date and time.");
			return;
		}

		this.setState({ saving: true });

		const payload: any = {
			admitSeasonTickets: f.admitSeasonTickets ?? null,
			captainSponsorId: f.captainSponsorId || null,
			competitionId: f.competitionId || null,
			fixtureDateTime: f.fixtureDateTime,
			location: Number(f.location) || 0,
			matchSponsorId: f.matchSponsorId || null,
			seasonId: f.seasonId || null,
			showOnSchedule: !!f.showOnSchedule,
			streamingUrl: f.streamingUrl || null,
			teamId: f.teamId,
			ticketTemplateId: f.ticketTemplateId || null,
			venueId: f.venueId || null
		};
		for (const a of AMENITIES) payload[a.key] = !!f[a.key];

		try {
			const res = f.id
				? await FixtureService.updateSingleById(f.id, payload)
				: await FixtureService.createSingle(payload);

			this.setState({ saving: false });

			if (!res.ok) {
				this.props.onError?.("Couldn't save that fixture. Please try again.");
				return;
			}

			// Create returns the DTO before the unit-of-work commit assigns an
			// id, so fall back to refetching rather than trusting a zero.
			let id = f.id;
			if (!id) {
				try {
					const body = await res.json();
					id = body?.id || 0;
				} catch { id = 0; }
			}
			this.props.onSaved(id);
		} catch {
			this.setState({ saving: false });
			this.props.onError?.("Couldn't save that fixture. Please try again.");
		}
	}

	render() {
		const { visible, onClose } = this.props;
		const { form, seasons, competitions, teams, venues, sponsors } = this.state;
		if (!visible) return null;

		const competitionsOfType = competitions.filter(
			(c) => form.competitionTypeId == null || c.competitionType === form.competitionTypeId
		);

		return (
			<Dialog
				footer={<>
					<Button className="p-button-text" label="Cancel" onClick={onClose} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save fixture"} onClick={() => this.save()} />
				</>}
				header={form.id ? "Edit fixture" : "Create fixture"}
				modal
				onHide={onClose}
				style={{ maxWidth: "95vw", width: 640 }}
				visible
			>
				<div className="cf-section">
					<h5>Admission</h5>

					<div className="cf-row">
						<label>Season</label>
						<Dropdown onChange={(e) => this.set({ seasonId: e.value })} optionLabel="name" optionValue="id" options={seasons} placeholder="Current season" showClear value={form.seasonId} />
					</div>

					<div className="cf-row">
						<label>Competition type</label>
						<Dropdown onChange={(e) => this.set({ competitionTypeId: e.value, competitionId: null })} optionLabel="label" optionValue="value" options={COMPETITION_TYPES} value={form.competitionTypeId} />
					</div>

					<div className="cf-row">
						<label>Competition</label>
						<Dropdown onChange={(e) => this.set({ competitionId: e.value })} optionLabel="name" optionValue="id" options={competitionsOfType} placeholder={competitionsOfType.length ? "Not set" : "None of this type yet"} showClear value={form.competitionId} />
					</div>

					<div className="cf-row">
						<label>Season tickets</label>
						<div>
							<Dropdown onChange={(e) => this.set({ admitSeasonTickets: e.value })} optionLabel="label" optionValue="value" options={SEASON_TICKET_OPTIONS} value={form.admitSeasonTickets ?? null} />
							<small>
								Overrides the competition rule for this fixture only &mdash; a cup tie you&rsquo;re including,
								or a testimonial you&rsquo;re not.
							</small>
						</div>
					</div>
				</div>

				<div className="cf-section">
					<h5>Opposition</h5>
					<div className="cf-row">
						<label>Visiting team</label>
						<Dropdown filter onChange={(e) => this.set({ teamId: e.value })} optionLabel="teamName" optionValue="id" options={teams} placeholder="Pick a team" value={form.teamId} />
					</div>
				</div>

				<div className="cf-section">
					<h5>Date and time</h5>
					<div className="cf-row">
						<label>Kick-off</label>
						<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.set({ fixtureDateTime: e.value })} showTime value={form.fixtureDateTime} />
					</div>
				</div>

				<div className="cf-section">
					<h5>Where</h5>
					<div className="cf-row">
						<label>Location</label>
						<Dropdown onChange={(e) => this.set({ location: e.value })} optionLabel="label" optionValue="value" options={LOCATIONS} value={form.location ?? 0} />
					</div>
					<div className="cf-row">
						<label>Venue</label>
						<Dropdown onChange={(e) => this.set({ venueId: e.value })} optionLabel="name" optionValue="id" options={venues} placeholder="Club default" showClear value={form.venueId} />
					</div>
					<div className="cf-row">
						<label>Facilities</label>
						<div className="cf-amenities">
							{AMENITIES.map((a) => (
								<label className={`cf-amenity${form[a.key] ? " on" : ""}`} key={a.key}>
									<input checked={!!form[a.key]} onChange={(e) => this.set({ [a.key]: e.target.checked })} type="checkbox" />
									{a.label}
								</label>
							))}
						</div>
					</div>
				</div>

				<div className="cf-section">
					<h5>Other details</h5>
					<div className="cf-row">
						<label>Match sponsor</label>
						<Dropdown onChange={(e) => this.set({ matchSponsorId: e.value })} optionLabel="name" optionValue="id" options={sponsors} placeholder="No sponsor" showClear value={form.matchSponsorId} />
					</div>
					<div className="cf-row">
						<label>Streaming link</label>
						<InputText onChange={(e) => this.set({ streamingUrl: e.target.value })} placeholder="https://…" value={form.streamingUrl || ""} />
					</div>
					<div className="cf-row">
						<label>Show on schedule</label>
						<div className="cf-switch">
							<InputSwitch checked={!!form.showOnSchedule} onChange={(e) => this.set({ showOnSchedule: e.value })} />
							<small>Off for behind-closed-doors games.</small>
						</div>
					</div>
				</div>

				<p className="cf-note">
					Scores, cards and the half-time draw stay on the matchday form under Scoreboard Management &mdash;
					nobody sets those when they&rsquo;re putting a game on sale.
				</p>
			</Dialog>
		);
	}
}
